import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- App origin for in-email links (conversation page) ---
const _resolvedOrigin = "https://helpgenie.ai";

const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (mirrors src/types/email-templates.ts) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface ConversationAutoDigestConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
  };
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

export interface AutoDigestConversationItem {
  id: string;
  createdAt: string;
  summary?: string | null;
  severity?: string | null;
  topic?: string | null;
}

interface ConversationAutoDigestData {
  agentId: string;
  agentName: string;
  agentDescription?: string | null;
  /** GenieBranding jsonb — used by the Email Signature block. */
  agentBranding?: Record<string, any> | null;
  periodStart: string;
  periodEnd: string;
  needsAttention: AutoDigestConversationItem[];
  autoHandled: AutoDigestConversationItem[];
  irrelevant: AutoDigestConversationItem[];
  recommendations?: { text: string; actionType: string | null }[];
  /** % of resolvable (needs_attention + auto_handled) conversations closed without the owner, 0-100. */
  resolutionRate?: number | null;
  timezone?: string;
}

const DEFAULT_LABELS: Record<string, string> = {
  auto_digest_summary: "Period Summary",
  auto_digest_needs_attention: "Needs Your Attention",
  auto_digest_auto_handled: "Handled Automatically",
  auto_digest_irrelevant: "Not Relevant / Informal",
  auto_digest_recommendations: "Recommendations",
  mobile_app_promo: "Help Genie in your pocket",
};

// Maps a recommendation's `actionType` (a `?section=` anchor id on the Genie
// detail page) to the button label shown in the digest email.
const SECTION_ACTION_LABELS: Record<string, string> = {
  "section-distribution": "Update deployment",
  "section-identity": "Update persona/voice",
  "section-prompts": "Update prompt/greeting",
  "section-knowledge": "Add to Knowledge Base",
  "section-documents": "Update documents",
  "section-branding": "Update branding",
  "section-mailbox": "Update mailbox/report settings",
  "section-tools": "Update integrations",
  "section-intelligence": "Update model settings",
  "section-extra": "Update settings",
};

function renderInlineButton(text: string, url: string, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  return `<a href="${url}" style="display:inline-block;padding:6px 14px;background-color:${colors.primary};color:#FFFFFF;text-decoration:none;font-weight:600;font-size:11px;border-radius:${metricButtonRadius};font-family:${fontFamily};box-shadow:${styles.shadowEnabled ? `0 2px 8px ${brandHexToRgba(colors.primary, 0.25)}` : "none"};">${text}</a>`;
}

function brandHexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveBlocks(config: ConversationAutoDigestConfig | null | undefined): ContentBlock[] {
  // Signature depends on genie branding a tenant may not have configured, so
  // it starts OFF when backfilled — unlike the rest of the value layer.
  const VALUE_LAYER_AUTOINJECT: string[] = ["signature"];
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  const defaults: ContentBlock[] = [
    { id: "auto_digest_summary", type: "auto_digest_summary", enabled: true, order: 0 },
    { id: "auto_digest_needs_attention", type: "auto_digest_needs_attention", enabled: true, order: 1 },
    { id: "auto_digest_recommendations", type: "auto_digest_recommendations", enabled: true, order: 2 },
    { id: "auto_digest_auto_handled", type: "auto_digest_auto_handled", enabled: true, order: 3 },
    { id: "auto_digest_irrelevant", type: "auto_digest_irrelevant", enabled: true, order: 4 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 5 },
    { id: "signature", type: "signature", enabled: false, order: 6 },
  ];

  const savedBlocks = ownSavedBlocks(config, defaults);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, defaults);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  metricButtonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

function formatDateRange(start: string, end: string, timezone = "UTC"): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, month: "short", day: "numeric", year: "numeric" });
    return `${formatter.format(new Date(start))} — ${formatter.format(new Date(end))}`;
  } catch {
    return `${start} — ${end}`;
  }
}

function renderAutoDigestSummary(block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.auto_digest_summary;

  const cell = (value: string, cellLabel: string, accent: string) => `
    <td width="33.33%" style="padding: 3px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: ${accent}0D; border: 1px solid ${accent}40; border-radius: 6px;">
        <tr>
          <td style="padding: 9px 11px;">
            <div style="font-size: 19px; font-weight: 800; color: ${accent}; line-height: 1; font-family: 'Baloo Paaji 2', ${fontFamily};">${value}</div>
            <div style="font-size: 9px; color: #6B7280; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px;">${cellLabel}</div>
          </td>
        </tr>
      </table>
    </td>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size:11px;color:#6B7280;font-family:${fontFamily};margin-bottom:10px;">${formatDateRange(data.periodStart, data.periodEnd, data.timezone)}</div>
              ${
                typeof data.resolutionRate === "number"
                  ? `
              <div style="display:flex;align-items:baseline;gap:6px;margin-bottom:10px;">
                <span style="font-size:26px;font-weight:800;color:${colors.primary};font-family:'Baloo Paaji 2', ${fontFamily};line-height:1;">${data.resolutionRate}%</span>
                <span style="font-size:11px;color:#6B7280;font-family:${fontFamily};">handled without you</span>
              </div>`
                  : ""
              }
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${cell(String(data.needsAttention.length), "Needs Attention", "#DC2626")}
                  ${cell(String(data.autoHandled.length), "Auto-Handled", colors.primary)}
                  ${cell(String(data.irrelevant.length), "Irrelevant", "#9CA3AF")}
                </tr>
              </table>
              ${
                data.agentId
                  ? `
              <table cellpadding="0" cellspacing="0" border="0" style="margin-top:12px;">
                <tr>
                  <td style="padding-right:8px;">${renderInlineButton("Review all flagged conversations", `${_resolvedOrigin}/genie-hub/inbox?tab=conversations&filter=needs_attention&date=today`, styles)}</td>
                  <td>${renderInlineButton("Adjust Auto triage sensitivity", `${_resolvedOrigin}/genie-hub/my-genies/${data.agentId}?section=section-mailbox`, styles)}</td>
                </tr>
              </table>`
                  : ""
              }
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderConversationGroup(
  items: AutoDigestConversationItem[],
  label: string,
  accentColor: string,
  styles: StyleContext,
  emptyMessage: string,
  // Only "needs attention" rows get a forward link — that's the group where
  // handing a specific conversation off to a teammate actually matters.
  forwardOptions?: { agentName: string },
): string {
  const { colors, fontFamily, metricButtonRadius } = styles;

  const rows =
    items.length === 0
      ? `<tr><td style="padding: 6px 0; font-size: 12px; color: #9CA3AF; font-family: ${fontFamily};">${emptyMessage}</td></tr>`
      : items
          .slice(0, 25)
          .map((c) => {
            const conversationUrl = `${_resolvedOrigin}/genie-hub/conversations/${c.id}`;
            // mailto: forward — opens the recipient's own mail client with a
            // pre-filled note and a link back to this conversation. No
            // backend send involved; the person the owner forwards to still
            // needs genie-hub access to open the link.
            const forwardLink = forwardOptions
              ? `<a href="mailto:?subject=${encodeURIComponent(`${forwardOptions.agentName} conversation needs attention`)}&body=${encodeURIComponent(`Flagging this conversation for you:\n\n${c.summary || "No summary available"}\n\n${conversationUrl}`)}" style="font-size:11px;color:#6B7280;text-decoration:none;font-family:${fontFamily};margin-left:10px;">Forward &rarr;</a>`
              : "";
            // Reply CTA: only for high-severity needs-attention rows — this is
            // where handing the owner a fast path to respond actually matters.
            const replyLink =
              forwardOptions && c.severity === "high"
                ? `<a href="mailto:?subject=${encodeURIComponent(`Re: ${forwardOptions.agentName} conversation`)}&body=${encodeURIComponent(`\n\n---\n${c.summary || "No summary available"}\n\n${conversationUrl}`)}" style="font-size:11px;color:${colors.primary};text-decoration:none;font-family:${fontFamily};margin-left:10px;">Reply to customer &rarr;</a>`
                : "";
            return `
        <tr>
          <td style="padding: 6px 0; border-bottom: 1px solid #E5EAF0; font-size: 12px; color: ${colors.text}; font-family: ${fontFamily};">
            <span style="font-weight:600;">${new Date(c.createdAt).toLocaleString()}</span>
            ${c.topic ? `<span style="margin-left:6px;padding:1px 6px;font-size:10px;font-weight:600;color:${colors.primary};background:${colors.primary}14;border-radius:10px;">${c.topic}</span>` : ""}
            ${c.severity ? `<span style="margin-left:6px;font-size:10px;font-weight:700;text-transform:uppercase;color:#DC2626;">${c.severity}</span>` : ""}
            <div style="color:#6B7280;margin-top:2px;">${c.summary || "No summary available"}</div>
            <a href="${conversationUrl}" style="font-size:11px;color:${colors.primary};text-decoration:none;font-family:${fontFamily};">View analysis &rarr;</a>${replyLink}${forwardLink}
          </td>
        </tr>`;
          })
          .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${accentColor};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderNeedsAttention(block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext): string {
  const label = block.label || DEFAULT_LABELS.auto_digest_needs_attention;
  return renderConversationGroup(
    data.needsAttention,
    label,
    "#DC2626",
    styles,
    "Nothing needed your attention this period.",
    { agentName: data.agentName },
  );
}

function renderAutoHandled(block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext): string {
  const label = block.label || DEFAULT_LABELS.auto_digest_auto_handled;
  return renderConversationGroup(data.autoHandled, label, styles.colors.primary, styles, "Nothing was handled automatically this period.");
}

function renderIrrelevant(block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext): string {
  const label = block.label || DEFAULT_LABELS.auto_digest_irrelevant;
  return renderConversationGroup(data.irrelevant, label, "#9CA3AF", styles, "Nothing irrelevant this period.");
}

function renderRecommendations(block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext): string {
  const recommendations = data.recommendations || [];
  if (recommendations.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.auto_digest_recommendations;

  const items = recommendations
    .map((r) => {
      const actionLabel = r.actionType ? SECTION_ACTION_LABELS[r.actionType] : undefined;
      const button =
        actionLabel && data.agentId
          ? `<div style="margin-top:6px;">${renderInlineButton(actionLabel, `${_resolvedOrigin}/genie-hub/my-genies/${data.agentId}?section=${r.actionType}`, styles)}</div>`
          : "";
      return `
        <tr>
          <td style="padding: 4px 0 10px 0; font-size: 12px; color: ${colors.text}; font-family: ${fontFamily}; line-height: 1.4;">
            &bull;&nbsp; ${r.text}
            ${button}
          </td>
        </tr>`;
    })
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${brandHexToRgba(colors.accent, 0.05)};border:1px solid ${brandHexToRgba(colors.accent, 0.3)};border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.accent};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderMobileAppPromo(block: ContentBlock, _data: ConversationAutoDigestData, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, metricButtonRadius } = styles;
  const heading = settings.heading || "Share Help Genie with your customers";
  const body = settings.body || "Get your team and customers on the app for instant support on every product.";
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  const headerColor = colors.headerColor || colors.text || "#28406F";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
              <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="padding-right: 8px;">
                    <a href="${appStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                  <td>
                    <a href="${playStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSignature(_block: ContentBlock, data: ConversationAutoDigestData, _styles: StyleContext): string {
  const html = renderSignatureBlockHtml(data.agentName || "Your Genie", data.agentDescription, data.agentBranding);
  if (!html) return "";
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

const blockRenderers: Record<
  string,
  (block: ContentBlock, data: ConversationAutoDigestData, styles: StyleContext) => string
> = {
  auto_digest_summary: renderAutoDigestSummary,
  auto_digest_needs_attention: renderNeedsAttention,
  auto_digest_auto_handled: renderAutoHandled,
  auto_digest_irrelevant: renderIrrelevant,
  auto_digest_recommendations: renderRecommendations,
  mobile_app_promo: renderMobileAppPromo,
  signature: renderSignature,
};

export function generateConversationAutoDigestEmailConfigurable(
  data: ConversationAutoDigestData,
  config?: ConversationAutoDigestConfig | null,
): string {
  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: undefined as string | undefined,
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  const headerTextColor = colors.headerColor || colors.text || "#28406F";
  const boxShadow = buttons.shadowEnabled ? `0 4px 20px ${brandHexToRgba(colors.primary || defaults.colors.primary, 0.1)}` : "none";
  const metricButtonRadius = getButtonRadius();

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    metricButtonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  const blocksHtml = enabledBlocks
    .map((block) => {
      const renderer = blockRenderers[block.type];
      if (!renderer) return "";
      return renderer(block, data, styleContext);
    })
    .filter(Boolean)
    .join("\n");

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && data.agentBranding?.signatureStyle?.enabled,
  );

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Conversation Summary</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">
              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${boxShadow}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">${data.agentName} — Daily Summary</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                ${blocksHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
}
